import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { User, Crown, SignOut, GearSix, Globe, CaretRight, CreditCard, Wallet, Check } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountPage() {
  const { user, logout, updateUser, isSuperAdmin } = useAuth();
  const { t, language, setLanguage, languageNames } = useLanguage();
  const navigate = useNavigate();
  const [vipStatus, setVipStatus] = useState(null);
  const [activeSub, setActiveSub] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showLanguageDialog, setShowLanguageDialog] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', vip_card_number: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchVip = async () => {
      try {
        const [vipRes, subRes] = await Promise.all([
          axios.get(`${API}/vip/status`, { withCredentials: true }),
          axios.get(`${API}/subscriptions/my`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setVipStatus(vipRes.data);
        setActiveSub(subRes.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchVip();
  }, []);

  useEffect(() => {
    if (user) {
      setForm({ name: user.name || '', phone: user.phone || '', vip_card_number: user.vip_card_number || '' });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/users/me`, form, { withCredentials: true });
      updateUser(res.data);
      toast.success(t('success'));
      setShowEditDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleUseFreeWash = async () => {
    try {
      const res = await axios.post(`${API}/vip/use-free-wash`, {}, { withCredentials: true });
      setVipStatus(prev => ({ ...prev, free_washes_available: res.data.remaining_free_washes }));
      toast.success('Free wash applied! Visit AutoSpa5 to use it.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to use free wash');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const progressPercent = vipStatus ? (vipStatus.progress_to_next_wash / (vipStatus.target || 7000)) * 100 : 0;
  const isCloseToReward = progressPercent >= 60;

  const languages = [
    { code: 'sq', name: 'Shqip', flag: '🇦🇱' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] safe-bottom">
      {/* Header */}
      <div className="p-4 pt-6">
        <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']">{t('account')}</h1>
      </div>

      {/* Profile Card */}
      <motion.div
        className="mx-4 mb-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="p-5 rounded-3xl bg-[#121212] border border-white/5">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#0ABAB5]/20 flex items-center justify-center overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-16 h-16 object-cover" />
              ) : (
                <User size={32} weight="duotone" className="text-[#0ABAB5]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold font-['Outfit'] truncate">{user?.name}</h2>
              <p className="text-white/50 text-sm truncate">{user?.email}</p>
              {user?.role !== 'user' && (
                <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-[#FF5A00]/20 text-[#FF5A00] rounded-full uppercase font-medium">
                  {user?.role?.replace('_', ' ')}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowEditDialog(true)}
              className="text-white/50 hover:text-white flex-shrink-0"
              data-testid="edit-profile-btn"
            >
              <GearSix size={24} />
            </Button>
          </div>
        </div>
      </motion.div>

      {/* VIP Card */}
      {vipStatus && (
        <motion.div
          className="mx-4 mb-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="vip-gradient rounded-3xl p-5 relative overflow-hidden">
            <div className="vip-shine absolute inset-0 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Crown size={22} weight="fill" className="text-[#FFD700]" />
                  <span className="font-semibold font-['Outfit']">{t('vipCard')}</span>
                </div>
                {vipStatus.free_washes_available > 0 && (
                  <Button
                    onClick={handleUseFreeWash}
                    className="h-8 px-4 bg-[#FFD700] hover:bg-[#FFD700]/90 text-black text-sm font-semibold rounded-full"
                    data-testid="use-free-wash-btn"
                  >
                    {t('useFreeWash')} ({vipStatus.free_washes_available})
                  </Button>
                )}
              </div>
              
              {/* Unlimited (Larje Pa Limit) users don't accrue VIP points — they already
                  get unlimited washes every 24h. Replace the points bar with a calm hero. */}
              {activeSub?.is_unlimited ? (
                <div className="mb-2 py-2" data-testid="vip-unlimited-hero">
                  <p className="text-lg font-bold text-white font-['Outfit'] leading-tight">
                    {t('vipUnlimitedTitle')}
                  </p>
                  <p className="text-white/60 text-xs mt-0.5">{t('vipUnlimitedSub')}</p>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <div className="flex justify-between items-end mb-2">
                      <span className="text-2xl font-bold font-['Outfit'] text-white">
                        {vipStatus.progress_to_next_wash.toLocaleString()}
                      </span>
                      <span className="text-white/50 text-sm">/ {(vipStatus.target || 7000).toLocaleString()} {t('lek')}</span>
                    </div>
                    <Progress value={progressPercent} className="h-3 bg-white/10" />
                  </div>
                  
                  <p className="text-white/50 text-xs mb-2">{t('vipExplain')}</p>
                  
                  {isCloseToReward && vipStatus.free_washes_available === 0 && (
                    <p className="text-[#0ABAB5] text-sm font-medium">{t('vipMotivation')}</p>
                  )}
                  {vipStatus.free_washes_available > 0 && (
                    <p className="text-[#FFD700] text-sm font-medium">{t('vipCongrats')}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Menu Items */}
      <div className="mx-4 space-y-2">
        {/* VIP Card */}
        <motion.button
          onClick={() => navigate('/vip-card')}
          className="w-full p-4 rounded-2xl bg-gradient-to-r from-[#FFD700]/10 via-[#0ABAB5]/5 to-transparent border border-[#FFD700]/25 flex items-center justify-between active:scale-[0.98] transition-transform"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          data-testid="vip-card-link"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FFD700]/20 flex items-center justify-center">
              <Crown size={20} weight="fill" className="text-[#FFD700]" />
            </div>
            <div className="text-left">
              <p className="text-white font-medium">{t('discoverVipCard')}</p>
              <p className="text-xs text-white/40">{t('vipCardSubtitle')}</p>
            </div>
          </div>
          <CaretRight size={18} className="text-white/30" />
        </motion.button>

        {/* Payment Methods */}
        <motion.button
          onClick={() => navigate('/payment')}
          className="w-full p-4 rounded-2xl bg-[#121212] border border-white/5 flex items-center justify-between active:scale-[0.98] transition-transform"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          data-testid="payment-link"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0ABAB5]/10 flex items-center justify-center">
              <Wallet size={20} className="text-[#0ABAB5]" />
            </div>
            <span>{t('paymentMethods')}</span>
          </div>
          <CaretRight size={18} className="text-white/30" />
        </motion.button>

        {/* Language */}
        <motion.button
          onClick={() => setShowLanguageDialog(true)}
          className="w-full p-4 rounded-2xl bg-[#121212] border border-white/5 flex items-center justify-between active:scale-[0.98] transition-transform"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          data-testid="language-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
              <Globe size={20} className="text-[#0ABAB5]" />
            </div>
            <span>{t('language')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white/50">{languages.find(l => l.code === language)?.flag} {languages.find(l => l.code === language)?.name}</span>
            <CaretRight size={18} className="text-white/30" />
          </div>
        </motion.button>

        {/* Admin Panel is OWNER-ONLY — Manager + Staff must never see it. */}
        {isSuperAdmin && (
          <motion.button
            onClick={() => navigate('/admin')}
            className="w-full p-4 rounded-2xl bg-[#121212] border border-white/5 flex items-center justify-between active:scale-[0.98] transition-transform"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            data-testid="admin-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FF5A00]/10 flex items-center justify-center">
                <GearSix size={20} className="text-[#FF5A00]" />
              </div>
              <span>{t('admin')} {t('dashboard')}</span>
            </div>
            <CaretRight size={18} className="text-white/30" />
          </motion.button>
        )}

        {/* Logout */}
        <motion.button
          onClick={handleLogout}
          className="w-full p-4 rounded-2xl bg-[#121212] border border-white/5 flex items-center justify-between active:scale-[0.98] transition-transform"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          data-testid="logout-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
              <SignOut size={20} className="text-red-400" />
            </div>
            <span className="text-red-400">{t('logout')}</span>
          </div>
        </motion.button>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit']">{t('editProfile')}</DialogTitle>
            <DialogDescription className="text-white/50">Update your profile information</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-white/60 mb-2 block">{t('name')}</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-14 bg-[#0a0a0a] border-white/10 rounded-xl text-white focus:border-[#0ABAB5]"
                data-testid="edit-name-input"
              />
            </div>

            <div>
              <label className="text-sm text-white/60 mb-2 block">{t('phone')}</label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="h-14 bg-[#0a0a0a] border-white/10 rounded-xl text-white focus:border-[#0ABAB5]"
                data-testid="edit-phone-input"
              />
            </div>

            {/* Iter30 — VIP card number (manual entry, no more NFC scan). */}
            <div>
              <label className="text-sm text-white/60 mb-2 block flex items-center gap-1.5">
                <Crown size={14} weight="fill" className="text-[#FFD700]" />
                {t('vipCardNumberLabel')}
              </label>
              <Input
                value={form.vip_card_number}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  setForm({ ...form, vip_card_number: digits });
                }}
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t('vipCardNumberPlaceholder')}
                className="h-14 bg-[#0a0a0a] border-white/10 rounded-xl text-white focus:border-[#FFD700] tabular-nums"
                data-testid="edit-vip-card-number-input"
              />
              <p className="text-[10px] text-white/40 mt-1.5 leading-snug">{t('vipCardNumberHelp')}</p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="flex-1 h-12 border-white/10"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleSaveProfile}
                disabled={saving}
                className="flex-1 h-12 bg-[#0ABAB5] hover:bg-[#0ABAB5]/90 text-black font-semibold"
                data-testid="save-profile-btn"
              >
                {saving ? t('loading') : t('save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Language Selection Dialog */}
      <Dialog open={showLanguageDialog} onOpenChange={setShowLanguageDialog}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit']">{t('language')}</DialogTitle>
            <DialogDescription className="text-white/50">Select your preferred language</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 mt-4">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setShowLanguageDialog(false);
                  toast.success(t('success'));
                }}
                className={`w-full p-4 rounded-xl border flex items-center justify-between transition-all ${
                  language === lang.code 
                    ? 'border-[#0ABAB5] bg-[#0ABAB5]/10' 
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{lang.flag}</span>
                  <span className="font-medium">{lang.name}</span>
                </div>
                {language === lang.code && (
                  <Check size={20} weight="bold" className="text-[#0ABAB5]" />
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
